import React, { useEffect, useState } from 'react';
import {
  Card, Table, Tag, Drawer, Descriptions, Select, Input, Button, message, Typography, Space,
} from 'antd';
import { apiClient } from 'Utils/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Withdrawal {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
  orderNumber: string | null;
  orderDate: string | null;
  watchShortCode: string | null;
  watchBrand: string | null;
  watchModel: string | null;
  watchReferenceNumber: string | null;
  reason: string | null;
  status: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
  internalNotes: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'red',
  IN_PROGRESS: 'orange',
  CLOSED: 'default',
};

const WithdrawalsPage: React.FC = () => {
  const [items, setItems] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState<Withdrawal | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/withdrawals');
      setItems(res.data?.withdrawals || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const open = (w: Withdrawal) => {
    setActive(w);
    setNotes(w.internalNotes || '');
  };

  const save = async (patch: any) => {
    if (!active) return;
    setSaving(true);
    try {
      const res = await apiClient.patch(`/api/admin/withdrawals/${active.id}`, patch);
      if (res.data?.status === 'success') {
        message.success('Aktualisiert');
        setActive(res.data.withdrawal);
        load();
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Eingegangen', dataIndex: 'createdAt', render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm') },
    { title: 'Kunde', key: 'name', render: (_: any, r: Withdrawal) => `${r.firstName} ${r.lastName}` },
    { title: 'E-Mail', dataIndex: 'email' },
    { title: 'Bestellung', dataIndex: 'orderNumber', render: (v: string | null) => v || <Text type="secondary">—</Text> },
    {
      title: 'Uhr',
      key: 'watch',
      render: (_: any, r: Withdrawal) => r.watchShortCode
        ? `${r.watchBrand || ''} ${r.watchModel || ''} (${r.watchShortCode})`
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: string) => <Tag color={STATUS_COLORS[s] || 'default'}>{s}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      render: (_: any, r: Withdrawal) => (
        <Button size="small" onClick={() => open(r)} data-testid={`view-wd-${r.id}`}>Öffnen</Button>
      ),
    },
  ];

  return (
    <Card>
      <Title level={3}>Widerrufe</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Übersicht aller eingegangenen Widerrufsanträge. Pflichtangaben: Vorname, Nachname, E-Mail.
      </Text>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns as any}
        pagination={{ pageSize: 20 }}
        data-testid="withdrawals-table"
      />

      <Drawer
        open={!!active}
        title={active ? `Widerruf · ${active.firstName} ${active.lastName}` : ''}
        width={640}
        onClose={() => setActive(null)}
      >
        {active && (
          <>
            <Space style={{ marginBottom: 16 }}>
              <Select
                value={active.status}
                style={{ width: 180 }}
                onChange={(v) => save({ status: v })}
                options={[
                  { value: 'OPEN', label: 'Offen' },
                  { value: 'IN_PROGRESS', label: 'In Bearbeitung' },
                  { value: 'CLOSED', label: 'Geschlossen' },
                ]}
                data-testid="wd-status-select"
              />
            </Space>

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Eingegangen">{dayjs(active.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Name">{active.firstName} {active.lastName}</Descriptions.Item>
              <Descriptions.Item label="E-Mail">{active.email}</Descriptions.Item>
              <Descriptions.Item label="Telefon">{active.phone || '—'}</Descriptions.Item>
              <Descriptions.Item label="Adresse">
                {[active.street, [active.postalCode, active.city].filter(Boolean).join(' '), active.countryCode]
                  .filter(Boolean).join(', ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Bestellnummer">{active.orderNumber || '—'}</Descriptions.Item>
              <Descriptions.Item label="Bestelldatum">{active.orderDate || '—'}</Descriptions.Item>
              <Descriptions.Item label="Uhr">
                {active.watchShortCode
                  ? `${active.watchBrand || ''} ${active.watchModel || ''}${active.watchReferenceNumber ? ' · Ref. ' + active.watchReferenceNumber : ''} (Code: ${active.watchShortCode})`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Grund">
                <div style={{ whiteSpace: 'pre-wrap' }}>{active.reason || '—'}</div>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 16 }}>
              <Text strong>Interne Notizen</Text>
              <TextArea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ marginTop: 6 }}
                data-testid="wd-notes-textarea"
              />
              <Button
                type="primary"
                style={{ marginTop: 8 }}
                loading={saving}
                onClick={() => save({ internalNotes: notes })}
                data-testid="wd-save-notes-btn"
              >
                Notizen speichern
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </Card>
  );
};

export default WithdrawalsPage;
